import { parseStringToDate } from '../../../utils/dayjsHelper.ts'
import {
  type ComandaAreaProps,
  type ComandaLineaEstadoProps,
  type ComandaLineaProps,
  type ComandaProps,
  ESTADO_COCINA,
  type EstadoCocinaProps,
  REST_AREA_GENERAL,
  TIPO_CAMBIO_COMANDA,
  TIPO_COMANDA,
} from '../../restaurante/interfaces/comanda.ts'

/** Estados que se muestran como columnas en la pantalla de cocina */
export type EstadoColumnaCocina =
  typeof ESTADO_COCINA.PENDIENTE | typeof ESTADO_COCINA.EN_PREPARACION | typeof ESTADO_COCINA.LISTO

export const ESTADOS_COLUMNA_COCINA: EstadoColumnaCocina[] = [
  ESTADO_COCINA.PENDIENTE,
  ESTADO_COCINA.EN_PREPARACION,
  ESTADO_COCINA.LISTO,
]

/** Color del tema por estado: se adapta a la paleta y al modo oscuro */
export type CocinaColor = 'info' | 'warning' | 'success' | 'error'

/** Una comanda vista desde un área: es la unidad de trabajo de la pantalla de cocina */
export interface TarjetaCocina {
  /** comandaId:area */
  key: string
  comanda: ComandaProps
  area: ComandaAreaProps
  estado: EstadoColumnaCocina
  lineas: ComandaLineaProps[]
  /**
   * true si no hay nada que preparar (cancelación o solo productos quitados):
   * cocina solo confirma con "Enterado".
   */
  soloAviso: boolean
}

export const keyComandaArea = (comandaId: string, area: string) => `${comandaId}:${area}`

/** Fecha de los strings del backend: acepta DD/MM/YYYY HH:mm[:ss] o ISO */
export const parseFechaComanda = (fecha?: string | null): Date | null => {
  if (!fecha) return null
  const dmy = parseStringToDate(fecha)
  if (dmy) return dmy
  const iso = new Date(fecha)
  return Number.isNaN(iso.getTime()) ? null : iso
}

export const formatHora = (fecha?: string | null) => {
  const d = parseFechaComanda(fecha)
  if (!d) return ''
  return d.toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })
}

/** Minutos transcurridos desde la fecha hasta `ahora` (redondeado hacia abajo) */
export const minutosDesde = (fecha: string | null | undefined, ahora: number) => {
  const d = parseFechaComanda(fecha)
  if (!d) return null
  return Math.max(0, Math.floor((ahora - d.getTime()) / 60000))
}

export const textoMinutos = (min: number | null) => {
  if (min === null) return ''
  if (min < 1) return 'ahora'
  if (min < 60) return `${min} min`
  return `${Math.floor(min / 60)} h ${min % 60} min`
}

/** Fecha de la última transición al estado indicado */
export const fechaTransicion = (area: ComandaAreaProps, estado: EstadoCocinaProps) => {
  const t = [...(area.transiciones ?? [])].reverse().find((x) => x.estado === estado)
  return t?.fecha ?? null
}

/** "Para llevar", "Delivery" o "Terraza · Mesa 5" */
export const getDestinoComanda = (comanda: Pick<ComandaProps, 'tipoPedido' | 'mesa' | 'espacio'>) => {
  if (comanda.tipoPedido === 'LLEVAR') return 'Para llevar'
  if (comanda.tipoPedido === 'DELIVERY') return 'Delivery'
  const mesa = comanda.mesa?.nombre ? `Mesa ${comanda.mesa.nombre}` : 'Mesa'
  return comanda.espacio?.descripcion ? `${comanda.espacio.descripcion} · ${mesa}` : mesa
}

export const getNumeroComanda = (comanda: ComandaProps) => comanda.numeroOrden || comanda.numeroPedido || 0

/** Una línea va a un área si está en sus áreas de impresión; sin áreas va a GENERAL */
export const lineaEnArea = (linea: ComandaLineaProps, area: string) => {
  const areas = linea.areasImpresion ?? []
  if (area === REST_AREA_GENERAL) return areas.length === 0
  return areas.includes(area)
}

/** Estado de la línea que cocina debe ver (el anterior si fue eliminada) */
export const estadoVisibleLinea = (linea: ComandaLineaProps): ComandaLineaEstadoProps | null =>
  linea.actual ?? linea.anterior

/** Detalles de la línea: modificadores, variaciones de receta y notas */
export const getDetallesLinea = (estado: ComandaLineaEstadoProps | null): string[] => {
  if (!estado) return []
  const detalles: string[] = []
  ;(estado.modificadores ?? []).forEach((m) => {
    const cantidad = m.cantidad && m.cantidad !== 1 ? `${m.cantidad} ` : ''
    const unidad = m.unidadMedida ? ` ${m.unidadMedida}` : ''
    detalles.push(`${cantidad}${m.nombre}${m.opcion ? ` ${m.opcion}` : ''}${unidad}`)
  })
  ;(estado.variaciones ?? []).forEach((v) => {
    const cantidad = v.unidadMedida ? ` ${v.cantidad} ${v.unidadMedida}` : ''
    if (v.tipo === 'SIN') detalles.push(`Sin ${v.nombre}`)
    else if (v.tipo === 'EXTRA') detalles.push(`Extra ${v.nombre}${cantidad}`)
    else detalles.push(`${v.nombre}${cantidad}`)
  })
  if (estado.notas?.length) detalles.push(`Nota: ${estado.notas.join(', ')}`)
  return detalles
}

export const getNombreLinea = (linea: ComandaLineaProps) => {
  const estado = estadoVisibleLinea(linea)
  const unidad = estado?.unidadMedida ? ` (${estado.unidadMedida})` : ''
  return `${linea.nombreArticulo || linea.codigoArticulo}${unidad}`
}

const esEstadoColumna = (estado: EstadoCocinaProps | null): estado is EstadoColumnaCocina =>
  !!estado && (ESTADOS_COLUMNA_COCINA as string[]).includes(estado)

/**
 * Convierte las comandas en tarjetas por área.
 * Solo incluye áreas con pantalla (estado != null) y abiertas, filtradas por la selección.
 */
export const getTarjetasCocina = (comandas: ComandaProps[], areasSeleccion: string[]): TarjetaCocina[] => {
  const todas = areasSeleccion.length === 0
  const tarjetas: TarjetaCocina[] = []
  comandas.forEach((comanda) => {
    comanda.areas.forEach((area) => {
      if (!esEstadoColumna(area.estado)) return
      if (!todas && !areasSeleccion.includes(area.area)) return
      const lineas = comanda.lineas.filter((l) => lineaEnArea(l, area.area))
      const soloAviso =
        comanda.tipo === TIPO_COMANDA.CANCELACION ||
        (lineas.length > 0 && lineas.every((l) => l.tipoCambio === TIPO_CAMBIO_COMANDA.ELIMINADO))
      tarjetas.push({
        key: keyComandaArea(comanda._id, area.area),
        comanda,
        area,
        estado: area.estado,
        lineas,
        soloAviso,
      })
    })
  })
  return tarjetas
}

// ==========================================
// Voz
// ==========================================

const numerosTexto: Record<number, string> = {
  1: 'un',
  2: 'dos',
  3: 'tres',
  4: 'cuatro',
  5: 'cinco',
  6: 'seis',
  7: 'siete',
  8: 'ocho',
  9: 'nueve',
  10: 'diez',
}

const cantidadVoz = (cantidad: number) => numerosTexto[cantidad] ?? String(cantidad)

const lineaVoz = (linea: ComandaLineaProps) => {
  const estado = estadoVisibleLinea(linea)
  const detalles = getDetallesLinea(estado)
  return `${cantidadVoz(estado?.cantidad ?? 1)} ${getNombreLinea(linea)}${detalles.length ? `, ${detalles.join(', ')}` : ''}`
}

/** Texto que se lee en voz alta cuando llega una comanda a cocina */
export const getVozTarjeta = (tarjeta: TarjetaCocina) => {
  const { comanda, lineas } = tarjeta
  const numero = getNumeroComanda(comanda)
  if (comanda.tipo === TIPO_COMANDA.CANCELACION) return `Orden ${numero}, cancelada`
  if (comanda.tipo === TIPO_COMANDA.NUEVA) return `Orden ${numero}, ${lineas.map(lineaVoz).join('; ')}`

  const frases = lineas.map((linea) => {
    if (linea.tipoCambio === TIPO_CAMBIO_COMANDA.NUEVO) return `agregar ${lineaVoz(linea)}`
    if (linea.tipoCambio === TIPO_CAMBIO_COMANDA.ELIMINADO) return `quitar ${lineaVoz(linea)}`
    const antes = linea.anterior?.cantidad ?? 0
    const ahora = linea.actual?.cantidad ?? 0
    if (ahora > antes) return `más ${cantidadVoz(ahora - antes)} ${getNombreLinea(linea)}`
    if (ahora < antes) return `menos ${cantidadVoz(antes - ahora)} ${getNombreLinea(linea)}`
    return `cambio ${lineaVoz(linea)}`
  })
  return `Orden ${numero}, modificación: ${frases.join('; ')}`
}

export const hablar = (texto: string) => {
  if (!('speechSynthesis' in window)) return
  window.speechSynthesis.cancel()
  const msg = new SpeechSynthesisUtterance(texto)
  msg.lang = 'es-MX'
  window.speechSynthesis.speak(msg)
}

// ==========================================
// Sonido corto (Web Audio, sin archivos)
// ==========================================

let audioCtx: AudioContext | null = null

/**
 * Dos tonos cortos y suaves. El navegador solo permite sonar después de una interacción
 * del usuario con la página; antes de eso falla en silencio.
 */
export const sonarAviso = () => {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    audioCtx = audioCtx ?? new Ctx()
    if (audioCtx.state === 'suspended') void audioCtx.resume()
    const ctx = audioCtx
    ;[880, 1320].forEach((freq, i) => {
      const inicio = ctx.currentTime + i * 0.18
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, inicio)
      gain.gain.exponentialRampToValueAtTime(0.15, inicio + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, inicio + 0.16)
      osc.connect(gain).connect(ctx.destination)
      osc.start(inicio)
      osc.stop(inicio + 0.17)
    })
  } catch {
    // Sin audio disponible: el aviso visual es suficiente
  }
}
